import axios from 'axios'
import { jwtDecode } from 'jwt-decode'
import type { User } from '@/types'
import type { UserRole } from '@/constants/roles'
import { AUTH } from '@/constants/ui'

const API_BASE = (import.meta.env.VITE_APP_API_GATEWAY_URL as string) ?? ''
const TOKEN_KEY = 'access_token'

interface TokenPayload {
  sub?: string
  username?: string
  name?: string
  role?: string
  exp: number
  iat: number
}

// ─── Token storage (sessionStorage — cleared on tab/browser close) ───────────

export function getStoredToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY)
}

export function decodeToken(token: string): TokenPayload | null {
  try {
    return jwtDecode<TokenPayload>(token)
  } catch {
    return null
  }
}

export function isAuthenticated(): boolean {
  const token = getStoredToken()
  if (!token) return false
  const payload = decodeToken(token)
  if (!payload) return false
  // Reject if expired (with 5-second grace period)
  return payload.exp * 1000 > Date.now() - 5_000
}

// ─── Login ───────────────────────────────────────────────────────────────────

/** Turns a failed login request into a message the user can act on. */
function loginErrorMessage(err: unknown): string {
  if (!axios.isAxiosError(err)) return AUTH.ERR_GENERIC
  if (!err.response) return AUTH.ERR_NETWORK
  const { status, data } = err.response
  if (status === 401 || status === 403) return AUTH.ERR_INVALID_CREDENTIALS
  // 5xx also covers the dev proxy / gateway failing to reach the backend
  if (status >= 500) return AUTH.ERR_SERVICE_DOWN
  return typeof data?.detail === 'string' && data.detail
    ? data.detail
    : AUTH.ERR_GENERIC
}

export async function loginUser(
  email: string,
  password: string
): Promise<{ token: string; user: User }> {
  let response
  try {
    response = await axios.post(
      `${API_BASE}/awgment-llm-local/login`,
      { username: email, password },
      { headers: { 'Content-Type': 'application/json' } }
    )
  } catch (err) {
    throw new Error(loginErrorMessage(err))
  }

  const data = response.data
  if (!data?.access_token) {
    throw new Error('Invalid credentials')
  }

  const token: string = data.access_token
  sessionStorage.setItem(TOKEN_KEY, token)

  // Derive display name from JWT payload
  let name = 'User'
  try {
    const payload = decodeToken(token)
    const raw = payload?.username ?? payload?.name ?? email
    name = raw.includes('@')
      ? raw
          .split('@')[0]
          .replace(/\./g, ' ')
          .replace(/\b\w/g, (c) => c.toUpperCase())
      : raw
  } catch {
    /* keep default */
  }
  sessionStorage.setItem('username', name)
  sessionStorage.setItem('email', email)

  // Fetch role from the check-role endpoint
  let role: UserRole = 'viewer'
  try {
    const roleRes = await axios.get(`${API_BASE}/awgment-llm-local/check-role`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    const raw: string = roleRes.data?.role ?? roleRes.data?.data?.role ?? 'viewer'
    role = raw.toLowerCase() as UserRole
    sessionStorage.setItem('role', role)
  } catch {
    /* proceed with default role */
  }

  const user = buildUser(name, email, role, token)
  return { token, user }
}

// ─── Logout ──────────────────────────────────────────────────────────────────

export function logout(): void {
  sessionStorage.removeItem(TOKEN_KEY)
  sessionStorage.removeItem('username')
  sessionStorage.removeItem('email')
  sessionStorage.removeItem('role')
}

// ─── Session restoration (called once on app startup) ────────────────────────

export function restoreUserFromSession(): User | null {
  if (!isAuthenticated()) return null
  const token = getStoredToken()!
  const username = sessionStorage.getItem('username') ?? 'User'
  const email = sessionStorage.getItem('email') ?? username
  const role = (sessionStorage.getItem('role') ?? 'viewer') as UserRole
  return buildUser(username, email, role, token)
}

// ─── Token expiry auto-logout ─────────────────────────────────────────────────

export function setupTokenExpiryTimer(onExpiry: () => void): () => void {
  const token = getStoredToken()
  if (!token) return () => {}
  const payload = decodeToken(token)
  if (!payload) return () => {}
  const msUntilExpiry = payload.exp * 1000 - Date.now()
  if (msUntilExpiry <= 0) {
    onExpiry()
    return () => {}
  }
  const timer = setTimeout(onExpiry, msUntilExpiry)
  return () => clearTimeout(timer)
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildUser(name: string, email: string, role: UserRole, token: string): User {
  // Try to get a stable id from the JWT subject claim
  let id = email
  try {
    const payload = decodeToken(token)
    id = payload?.sub ?? email
  } catch {
    /* keep email as id */
  }

  const avatar = name
    .split(' ')
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
    .slice(0, 2)

  return { id, name, email, role, avatar }
}
